import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./useAuth";
import { fetchNotebook, saveNotebook } from "../lib/api";

const EMPTY = { saved: [], favorites: [], notes: [] };

const NotebookContext = createContext(null);

export const NotebookProvider = ({ children }) => {
  const { user } = useAuth();
  const [data, setData] = useState(EMPTY);
  const dataRef = useRef(EMPTY);
  const userRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Load this Google account's notebook on login; reset to empty on logout.
  // `loadedRef` guards against auto-saving the empty initial state over the
  // server copy before the fetch resolves (which would wipe saved items).
  useEffect(() => {
    userRef.current = user;
    loadedRef.current = false;
    if (user) {
      fetchNotebook()
        .then((d) => setData({ saved: d.saved || [], favorites: d.favorites || [], notes: d.notes || [] }))
        .catch(() => setData(EMPTY))
        .finally(() => { loadedRef.current = true; });
    } else {
      setData(EMPTY);
    }
  }, [user]);

  const apply = useCallback((next) => {
    dataRef.current = next;
    setData(next);
    if (userRef.current && loadedRef.current) saveNotebook(next).catch(() => {});
  }, []);

  const isSaved = useCallback((id) => data.saved.some((b) => b.id === id), [data]);
  const isFavorite = useCallback((id) => data.favorites.some((b) => b.id === id), [data]);

  const toggleSave = useCallback((business) => {
    const prev = dataRef.current;
    const exists = prev.saved.some((b) => b.id === business.id);
    apply({
      ...prev,
      saved: exists ? prev.saved.filter((b) => b.id !== business.id) : [...prev.saved, business],
    });
    return !exists;
  }, [apply]);

  const toggleFavorite = useCallback((business) => {
    const prev = dataRef.current;
    const exists = prev.favorites.some((b) => b.id === business.id);
    apply({
      ...prev,
      favorites: exists ? prev.favorites.filter((b) => b.id !== business.id) : [...prev.favorites, business],
    });
    return !exists;
  }, [apply]);

  const addNote = useCallback((note) => {
    const prev = dataRef.current;
    apply({
      ...prev,
      notes: [
        { id: Date.now().toString(), text: note.text, businessName: note.businessName || null, createdAt: new Date().toISOString() },
        ...prev.notes,
      ],
    });
  }, [apply]);

  const deleteNote = useCallback((id) => {
    const prev = dataRef.current;
    apply({ ...prev, notes: prev.notes.filter((n) => n.id !== id) });
  }, [apply]);

  return (
    <NotebookContext.Provider value={{ data, isSaved, isFavorite, toggleSave, toggleFavorite, addNote, deleteNote }}>
      {children}
    </NotebookContext.Provider>
  );
};

export const useNotebook = () => useContext(NotebookContext);
